import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Plus, Minus, Truck, Shield, CheckCircle, User, CalendarClock } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { SEO } from "../components/SEO";
import { Toast } from "../components/Toast";
import { StarRating } from "../components/StarRating";
import { WishlistButton } from "../components/WishlistButton";
import AutoReorderModal from "../components/AutoReorderModal";

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [toast, setToast] = useState(null);
  const [showAutoReorder, setShowAutoReorder] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const [prodRes, relRes, revRes, statsRes] = await Promise.all([
          axios.get(`${API}/products/${id}`),
          axios.get(`${API}/products/${id}/related?limit=8`).catch(() => ({ data: [] })),
          axios.get(`${API}/products/${id}/reviews`).catch(() => ({ data: [] })),
          axios.get(`${API}/reviews/stats/${id}`).catch(() => ({ data: null })),
        ]);
        setProduct(prodRes.data);
        setRelatedProducts(relRes.data || []);
        setReviews(revRes.data || []);
        setReviewStats(statsRes.data);
      } catch (err) {
        console.error("Failed to fetch product:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleAddToCart = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    const success = await addToCart(product.id, cappedQty);
    if (success) {
      setToast({ message: "Added to cart!", type: "success" });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-500">Product not found</p>
        <Link to="/products" className="text-[#00a99d] hover:underline">Back to products</Link>
      </div>
    );
  }

  const savings = product.original_price ? Math.round((1 - product.price / product.original_price) * 100) : 0;
  const displayRating = reviewStats?.avg_rating || product.avg_rating || 5.0;
  const displayCount = reviewStats?.review_count || product.review_count || 0;
  const stock = typeof product.stock === "number" ? product.stock : 0;
  const outOfStock = stock <= 0;
  const lowStock = stock > 0 && stock < 10;
  const cappedQty = Math.min(quantity, Math.max(stock, 1));

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <SEO title={product.name} description={`Buy ${product.name} for £${product.price.toFixed(2)}. ${product.brand || ''} - Free delivery on orders over £30.`} />
      <div className="breadcrumb mb-4 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-[#00a99d] hover:underline font-medium"
          data-testid="back-button"
        >
          <span aria-hidden="true">&larr;</span> Back
        </button>
        <span className="text-gray-300">|</span>
        <Link to="/">Home</Link> / <Link to="/products">Products</Link>
        {product.category && (
          <>
            {" "}/ <Link to={`/products?category=${encodeURIComponent(product.category)}`} data-testid="breadcrumb-category">{product.category}</Link>
          </>
        )}
        {" "}/ <span>{product.name}</span>
      </div>

      <div className="grid md:grid-cols-2 gap-8" data-testid="product-detail">
        <div className="card p-8">
          <img src={product.image_url || "https://via.placeholder.com/400"} alt={product.name} className="w-full max-h-96 object-contain" />
        </div>

        <div>
          {product.brand && (
            <span className="text-sm font-medium text-[#00a99d] uppercase tracking-wide">{product.brand}</span>
          )}
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mt-2">{product.name}</h1>
          <p className="text-gray-500 mt-1">SKU: {product.sku}</p>

          {/* Star Rating */}
          <div className="mt-2 flex items-center gap-2" data-testid="product-rating">
            <StarRating rating={displayRating} size="w-5 h-5" />
            <span className="text-sm font-medium text-gray-700">{displayRating}</span>
            <span className="text-sm text-gray-500">({displayCount} {displayCount === 1 ? 'review' : 'reviews'})</span>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <span className="text-3xl font-bold text-[#00a99d]">&pound;{product.price.toFixed(2)}</span>
            {product.original_price && (
              <>
                <span className="text-lg text-gray-400 line-through">&pound;{product.original_price.toFixed(2)}</span>
                <span className="price-savings">Save {savings}%</span>
              </>
            )}
          </div>
          <p className="text-sm text-gray-500">ex VAT</p>

          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded" data-testid="stock-banner">
            {outOfStock ? (
              <p className="text-red-700 font-medium flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Out of stock — please check back soon or contact us for restock dates.
              </p>
            ) : lowStock ? (
              <p className="text-orange-700 font-medium flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Only {stock} left in stock — order soon. Free next-day delivery available.
              </p>
            ) : (
              <p className="text-green-700 font-medium flex items-center gap-2">
                <Truck className="w-5 h-5" />
                In Stock ({stock} available) — Free next-day delivery available
              </p>
            )}
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={outOfStock}
                className="w-10 h-10 border rounded flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-12 text-center font-medium">{cappedQty}</span>
              <button 
                onClick={() => setQuantity(Math.min(stock || 1, quantity + 1))}
                disabled={outOfStock || quantity >= stock}
                className="w-10 h-10 border rounded flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <button
            onClick={handleAddToCart}
            disabled={outOfStock}
            className={`btn-primary w-full mt-6 py-4 text-lg ${outOfStock ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="add-to-cart-btn"
          >
            <ShoppingCart className="w-5 h-5" />
            {outOfStock ? "Out of Stock" : `Add to Cart - £${(product.price * cappedQty).toFixed(2)}`}
          </button>

          <div className="mt-3">
            <WishlistButton productId={product.id} variant="button" className="w-full py-3" />
          </div>

          <button
            onClick={() => {
              if (!user) { navigate("/login"); return; }
              setShowAutoReorder(true);
            }}
            className="w-full mt-3 inline-flex items-center justify-center gap-2 text-sm font-semibold text-[#00a99d] hover:text-[#008c82] border-2 border-dashed border-[#00a99d]/40 hover:border-[#00a99d] py-3 rounded-lg transition"
            data-testid="auto-reorder-cta"
          >
            <CalendarClock className="w-4 h-4" />
            Set up Auto-Reorder reminders
          </button>

          <div className="mt-8">
            <h3 className="font-semibold text-gray-800 mb-2">Description</h3>
            <p className="text-gray-600">{product.description}</p>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <Shield className="w-4 h-4 text-[#00a99d]" />
              3 Year Warranty
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Truck className="w-4 h-4 text-[#00a99d]" />
              Free Returns
            </div>
          </div>
        </div>
      </div>

      {/* Customer Reviews Section */}
      <div className="mt-12" data-testid="reviews-section">
        <h2 className="text-xl font-bold text-gray-800 mb-6">Customer Reviews</h2>
        
        {/* Rating Summary */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-6 flex-wrap">
            <div className="text-center">
              <p className="text-4xl font-bold text-gray-800">{displayRating}</p>
              <StarRating rating={displayRating} size="w-5 h-5" />
              <p className="text-sm text-gray-500 mt-1">{displayCount} reviews</p>
            </div>
            {reviewStats?.distribution && (
              <div className="flex-1 min-w-[200px] space-y-1">
                {[5, 4, 3, 2, 1].map(star => {
                  const count = reviewStats.distribution[star] || 0;
                  const pct = displayCount > 0 ? (count / displayCount) * 100 : 0;
                  return (
                    <div key={star} className="flex items-center gap-2 text-sm">
                      <span className="w-6 text-right text-gray-600">{star}</span>
                      <StarRating rating={star} size="w-3 h-3" />
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-8 text-gray-500 text-xs">{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Individual Reviews */}
        {reviews.length > 0 ? (
          <div className="space-y-4" data-testid="reviews-list">
            {reviews.map((review) => (
              <div key={review.id} className="card p-5" data-testid={`review-${review.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#00a99d]/10 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-[#00a99d]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800">{review.reviewer_name}</span>
                      {review.verified_purchase && (
                        <span className="inline-flex items-center gap-1 text-xs text-[#00a99d] bg-[#00a99d]/10 px-2 py-0.5 rounded-full">
                          <CheckCircle className="w-3 h-3" /> Verified Purchase
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <StarRating rating={review.rating} size="w-4 h-4" />
                      <span className="text-sm text-gray-500">
                        {new Date(review.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                    <p className="font-medium text-gray-800 mt-2">{review.title}</p>
                    <p className="text-gray-600 text-sm mt-1">{review.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card p-6 text-center text-gray-500">
            <p>No detailed reviews yet for this product.</p>
            <p className="text-sm mt-1">Based on {displayCount} customer ratings.</p>
          </div>
        )}
      </div>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div className="mt-12" data-testid="related-products">
          <h2 className="text-xl font-bold text-gray-800 mb-6">Frequently Bought Together</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {relatedProducts.slice(0, 4).map((rp) => (
              <Link key={rp.id} to={`/products/${rp.id}`} className="card p-4 hover:shadow-lg transition-shadow group" data-testid={`related-product-${rp.id}`}>
                <div className="aspect-square mb-3 overflow-hidden rounded">
                  <img 
                    src={rp.image_url || "https://via.placeholder.com/200"} 
                    alt={rp.name} 
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform" 
                  />
                </div>
                {rp.brand && <p className="text-xs font-medium text-[#00a99d] uppercase">{rp.brand}</p>}
                <p className="text-sm font-medium text-gray-800 line-clamp-2 mt-1">{rp.name}</p>
                <p className="text-lg font-bold text-[#00a99d] mt-2">&pound;{rp.price.toFixed(2)}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {showAutoReorder && (
        <AutoReorderModal
          product={product}
          defaultQty={cappedQty}
          onClose={() => setShowAutoReorder(false)}
          onSuccess={() => setToast({ message: "Auto-Reorder activated", type: "success" })}
        />
      )}
    </div>
  );
}
